/**
 * Design tokens – the single source of truth for every visual value in the app.
 *
 * Nothing below depends on MUI: `theme.ts` maps these primitives onto the MUI
 * theme, and components read them through the theme (or directly, for values
 * MUI has no slot for). Keeping them framework-free means the palette can be
 * reused by charts, e-mail templates or a future design-token export.
 */

/** Raw colour ramps. Only `theme.ts` should pick from these directly. */
export const palette = {
  /**
   * Soft steel blue. Mid stops are the brand; 800+ is ink on a tint, not chrome.
   * Kept lighter than navy (#172554 / #0F172A) so large surfaces stay open.
   */
  blue: {
    50: '#F4F8FD',
    100: '#E7F1FB',
    200: '#C9DEF6',
    300: '#A4C6EE',
    400: '#78A6E2',
    500: '#548CD4',
    600: '#3A74B8',
    700: '#2E629E',
    800: '#24507A',
    900: '#1C4060',
    950: '#16324C',
  },
  /** Teal – success / healthy service state. */
  teal: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
  },
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  amber: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
  violet: {
    400: '#a78bfa',
    500: '#8b5cf6',
    600: '#7c3aed',
  },
  white: '#ffffff',
  black: '#000000',
} as const;

/** 4px base scale. `spacing(n)` in MUI resolves to `n * 4px`. */
export const spacingBase = 4;

export const radii = {
  none: 0,
  sm: 4,
  md: 4,
  lg: 6,
  xl: 6,
  pill: 999,
} as const;

export const typography = {
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    'Segoe UI',
    'Roboto',
    'sans-serif',
  ].join(', '),
  fontFamilyMono: ['JetBrains Mono', 'IBM Plex Mono', 'ui-monospace', 'monospace'].join(', '),
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 600,
  },
} as const;

/**
 * Flat surfaces. Focus rings follow the soft blue; elevation stays neutral
 * so a coloured shadow does not haze the black theme.
 */
export const shadows = {
  light: {
    xs: 'none',
    sm: '0 1px 2px rgba(0,0,0,0.25)',
    md: '0 4px 12px rgba(0,0,0,0.4)',
    lg: '0 4px 12px rgba(0,0,0,0.4)',
    xl: '0 4px 12px rgba(0,0,0,0.4)',
    focus: '0 0 0 2px rgba(58, 116, 184, 0.7)',
  },
  dark: {
    xs: 'none',
    sm: '0 1px 2px rgba(0,0,0,0.25)',
    md: '0 4px 12px rgba(0,0,0,0.4)',
    lg: '0 4px 12px rgba(0,0,0,0.4)',
    xl: '0 4px 12px rgba(0,0,0,0.4)',
    focus: '0 0 0 2px rgba(126, 174, 245, 0.7)',
  },
} as const;

/**
 * Shadow slots with widened values: `shadows` is `as const`, so the literal
 * types of the light set would not accept the dark one. Consumers (and the MUI
 * theme augmentation) need the shape, not the exact strings.
 */
export type ShadowTokens = Record<keyof typeof shadows.light, string>;

export const layout = {
  sidebarRailWidth: 68,
  sidebarPanelWidth: 220,
  sidebarWidth: 288,
  sidebarCollapsedWidth: 68,
  topbarHeight: 48,
  topbarHeightMobile: 56,
  bottomNavHeight: 56,
  /** Minimum tap target on `xs` (WCAG 2.5.5 / Apple HIG). */
  touchTarget: 44,
  contentMaxWidth: 10000,
  contentPaddingMobile: 16,
  contentPaddingDesktop: 12,
} as const;

export const zIndex = {
  sidebar: 1200,
  topbar: 1100,
  aiAssistant: 1250,
  modal: 1300,
  toast: 1400,
} as const;

export const transitions = {
  fast: '100ms ease-out',
  normal: '150ms ease-out',
  slow: '250ms ease-out',
} as const;

/**
 * Table chrome. Light values are the Gmail hairline scale.
 * Dark values stay a few percent off the canvas so a row hover never flashes white.
 * `readHover` is only for a future `highlightUnread` row, one step under `hover`.
 */
export const tableSurface = {
  light: {
    row: {
      default: '#FFFFFF',
      hover: '#F9FAFB',
      selected: '#EFF8FF',
      readHover: '#F2F4F7',
    },
    hairline: '#EAECF0',
  },
  dark: {
    row: {
      default: 'rgba(255,255,255,0.02)',
      hover: 'rgba(255,255,255,0.04)',
      selected: 'rgba(58,116,184,0.12)',
      readHover: 'rgba(255,255,255,0.06)',
    },
    hairline: 'rgba(255,255,255,0.08)',
  },
} as const;

/**
 * Semantic colours for domain states. These are the only colours business
 * components should reference, so a palette change never means grepping for
 * hex values across pages.
 */
export type SemanticTone = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'critical';

export interface ToneColors {
  /** Text / icon colour. */
  main: string;
  /** Tinted surface for chips and callouts. */
  surface: string;
  /** Border for tinted surfaces. */
  border: string;
  /** Text colour that meets WCAG AA on `surface`. */
  contrastText: string;
}

export const tones: Record<'light' | 'dark', Record<SemanticTone, ToneColors>> = {
  light: {
    success: {
      main: palette.teal[600],
      surface: palette.teal[50],
      border: palette.teal[200],
      contrastText: palette.teal[800],
    },
    warning: {
      main: palette.amber[600],
      surface: palette.amber[50],
      border: palette.amber[200],
      contrastText: palette.amber[800],
    },
    error: {
      main: palette.red[600],
      surface: palette.red[50],
      border: palette.red[200],
      contrastText: palette.red[800],
    },
    critical: {
      main: palette.red[700],
      surface: palette.red[100],
      border: palette.red[300],
      contrastText: palette.red[900],
    },
    info: {
      main: palette.blue[600],
      surface: palette.blue[50],
      border: palette.blue[200],
      contrastText: palette.blue[800],
    },
    neutral: {
      main: palette.slate[500],
      surface: palette.slate[100],
      border: palette.slate[300],
      contrastText: palette.slate[700],
    },
  },
  dark: {
    success: {
      main: palette.teal[400],
      surface: 'rgba(20, 184, 166, 0.14)',
      border: 'rgba(20, 184, 166, 0.36)',
      contrastText: palette.teal[200],
    },
    warning: {
      main: palette.amber[400],
      surface: 'rgba(245, 158, 11, 0.14)',
      border: 'rgba(245, 158, 11, 0.36)',
      contrastText: palette.amber[200],
    },
    error: {
      main: palette.red[400],
      surface: 'rgba(239, 68, 68, 0.14)',
      border: 'rgba(239, 68, 68, 0.36)',
      contrastText: palette.red[200],
    },
    critical: {
      main: palette.red[300],
      surface: 'rgba(239, 68, 68, 0.22)',
      border: 'rgba(239, 68, 68, 0.48)',
      contrastText: palette.red[100],
    },
    info: {
      main: palette.blue[300],
      surface: 'rgba(58, 116, 184, 0.16)',
      border: 'rgba(58, 116, 184, 0.38)',
      contrastText: palette.blue[100],
    },
    neutral: {
      main: palette.slate[400],
      surface: 'rgba(148, 163, 184, 0.14)',
      border: 'rgba(148, 163, 184, 0.32)',
      contrastText: palette.slate[300],
    },
  },
};

/** Ordered series colours for Recharts, so every chart is consistent. */
export const chartSeries = [
  palette.blue[600],
  palette.teal[500],
  palette.amber[500],
  palette.violet[500],
  palette.red[500],
  palette.blue[300],
  palette.teal[700],
  palette.slate[500],
] as const;
